/**
 * DraftingDetail - detail view for /drafting/:draftingUuid (AB#1573).
 *
 * Resolves the document by uuid (could be a draft OR a strategy - the
 * URL doesn't say which). Strategy: check the existing Redux buckets
 * first; if not found there, try `kind='draft'` then `kind='strategy'`.
 *
 * THE PAGE IS A DETAIL SURFACE. The document is the CANVAS; the version
 * history, the activity log and the matter linkage are PANES floating over
 * its right edge - they dock, stack, reorder, resize and are remembered per
 * user, and every one of them was a bespoke drawer, tiled column or header
 * badge strip before. See `./surface/layout` for what each replaced.
 *
 * ONE HEADER ROW. The document's line (title, type, matter, age) is the tab
 * bar's `leading` slot, so it shares a row with the View/Edit modes and the
 * pane rail rather than stacking above them. The linked matter is UNCHANGED
 * inside that line - the draft↔matter relationship is being designed
 * separately, so nothing here alters it or its pane.
 *
 * Once the document loads, the canvas mounts CollaborativeDocumentEditor with
 * `readOnly` bound to the reading mode. Yjs handles autosave through the
 * existing PubSubCollaborationService - no explicit Save button needed.
 */

import { useEffect, useMemo, useContext, useCallback, useRef, useState } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import GlassCard from '../shared/brand/GlassCard';
import { useHistory } from 'react-router-dom';
import {
  PiEyeLight, PiPencilSimpleLight,
  PiSparkleLight, PiArrowClockwiseLight,
  PiArrowsClockwiseLight,
  PiSlidersHorizontalLight,
  PiDownloadSimpleLight, PiTrashLight,
  PiClockCounterClockwiseLight,
  PiBriefcaseLight,
  PiFileTextLight,
  PiArrowsOutLight,
  PiPulseLight,
  PiUsersLight,
  PiScalesLight,
  PiListChecksLight,
  PiScrollLight,
  PiClockLight,
} from 'react-icons/pi';
import PlaybookPickerModal from '../BatchTables/PlaybookPickerModal';
import { startReviewFromDraft } from '../../utils/reviewFromDocApi';
import { markAsContract } from '../../utils/contractsApi';
import { rerunDraftFromSource } from '../../utils/draftSourceApi';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import Button from '../shared/TailwindComponents/Button';
import DocumentIntelligence from './DocumentIntelligence';
import Badge from '../shared/TailwindComponents/Badge';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import type { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  fetchDraftingItem,
  generateDrafting,
  downloadDrafting,
  deleteDrafting,
  clearCurrentItem,
  clearStreamingState,
  fetchDraftingContent,
  fetchDraftingEditorJson,
  saveDraftingContent,
  fetchDraftingCollaborators,
  addDraftingCollaborator,
  updateDraftingCollaboratorAccess,
  removeDraftingCollaborator,
  patchDrafting,
  markGenerating,
} from '../../store/slices/draftingSlice';
import { openDraftingContentStream } from '../../services/streaming/draftingContentStream';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useDraftingPubSub } from '../../hooks/useDraftingPubSub';
import { DRAFTING_READ_ONLY, DRAFTING_SHARING_ENABLED } from './draftingConfig';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import DetailFrame from '../shared/Frames/DetailFrame';
import TimeSavedChip from '../shared/TimeSavedChip';
import { UserContext } from '../../contexts/UserContext';

import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import DetailPageSurface, { DetailPane } from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import { MetaDot } from '../shared/surface/ListRow';
import { feedTimeLabel } from '../shared/feed/feedTime';
import { DRAFTING_SURFACE, DraftingPanelId, useDraftingWorkspace } from './surface';
import useDraftingVersions from './surface/useDraftingVersions';
import useDraftingActivity from './surface/useDraftingActivity';
import DraftingCanvas from './surface/DraftingCanvas';
import DraftingVersionsPane from './surface/DraftingVersionsPane';
import DraftingActivityPane from './surface/DraftingActivityPane';
import AIUsagePanel from '../shared/aiUsage/AIUsagePanel';
import DraftingAccessPane from './surface/DraftingAccessPane';
import { draftTitle, draftTypeLabel } from './draftLabels';

interface DraftingDetailProps {
  draftingUuid: string;
}

function DraftingDetail({ draftingUuid }: Readonly<DraftingDetailProps>) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const userCtx = useContext(UserContext);

  // Look in both buckets so we can resolve kind from the list cache
  // without a round trip when the user clicked through from the list.
  const cachedItem = useAppSelector((s: RootState) => {
    const fromDrafts = s.drafting.drafts.find((d) => d.uuid === draftingUuid);
    if (fromDrafts) return fromDrafts;
    return s.drafting.strategies.find((d) => d.uuid === draftingUuid);
  });
  // Whose access rows are "you" in the manage panel. Same source as Tables.
  const userInfo = useAppSelector((s: RootState) => s.user.userInfo);
  const currentItem = useAppSelector((s: RootState) => s.drafting.currentItem);
  const itemLoading = useAppSelector((s: RootState) => s.drafting.itemLoading);
  const error = useAppSelector((s: RootState) => s.drafting.error);
  const itemError = useAppSelector((s: RootState) => s.drafting.itemError);
  const currentContent: string | null = useAppSelector((s: RootState) => s.drafting.currentContent);
  const currentEditorJson = useAppSelector((s: RootState) => s.drafting.currentEditorJson);
  const currentContentBlob: Blob | null = useAppSelector((s: RootState) => s.drafting.currentContentBlob);
  const streamingState = useAppSelector((s: RootState) => s.drafting.streamingState);
  const contentBytesReceived = useAppSelector((s: RootState) => s.drafting.contentBytesReceived);
  const processingMessage = useAppSelector((s: RootState) => s.drafting.processingMessage);
  const processingProgress = useAppSelector((s: RootState) => s.drafting.processingProgress);
  // True while a generation is queued/running for this uuid. Set on generate,
  // cleared on the terminal PubSub event - survives clearCurrentItem, so it's
  // the reliable "is this generating right now" signal across navigation.
  const isGenerating = useAppSelector((s: RootState) => s.drafting.generatingIds.includes(draftingUuid));

  const item = currentItem?.uuid === draftingUuid ? currentItem : cachedItem;

  /**
   * The workspace: the pane layout and the reading mode, from the ONE shared
   * hook. Keyed by document AND by user - see `surface/layout`.
   */
  const { panels, activeTab, goToTab } = useDraftingWorkspace({ entityId: draftingUuid });

  const handleVersionsRestored = useCallback(() => {
    if (!item) return;
    // The restored version is now current - reload the live content + row.
    dispatch(fetchDraftingContent({ kind: item.kind, uuid: item.uuid }));
    dispatch(fetchDraftingItem({ kind: item.kind, uuid: item.uuid }));
  }, [dispatch, item]);

  // Persist an edit from the rich editor. The editor emits HTML; the backend
  // converts to markdown and echoes it back, which the slice writes into
  // `currentContent` so switching to View reflects the change immediately.
  const handleSaveContent = useCallback((html: string, json?: unknown, saveKind: 'auto' | 'manual' = 'auto') => {
    if (!item) return Promise.resolve();
    // Return the promise (unwrapped) so the editor shows real Saving/Saved/Failed
    // status and retries on failure instead of dropping the edit. saveKind='auto'
    // for autosave/blur/unmount (kept out of the Versions panel); 'manual' when
    // the user clicks Save (a restorable milestone). `json` is the lossless Tiptap
    // doc - forwarded so the backend can persist the editor.json sidecar; without
    // it the sidecar is never written and the JSON round-trip never engages.
    return dispatch(saveDraftingContent({
      kind: item.kind, uuid: item.uuid, content: html, contentFormat: 'html', saveKind,
      editorJson: json,
    })).unwrap();
  }, [dispatch, item]);

  /**
   * Version history - list AND preview in one place, so the rail pill can
   * carry a count and a restore does not have to travel through three
   * components to reload the document. Only fetched once the document has
   * something to snapshot.
   */
  const versions = useDraftingVersions({
    kind: item?.kind,
    uuid: item?.uuid,
    enabled: item?.status === 'completed',
    onRestored: handleVersionsRestored,
  });
  const {
    previewVersionId, previewContent, previewLoading,
    selectVersion, refresh: refreshVersions, count: versionCount,
  } = versions;

  /**
   * The audit trail. Owned by the page for the same reason the versions are:
   * the REFRESH control belongs in the pane's header, next to its close
   * button, rather than as a second toolbar row inside the body.
   */
  const activity = useDraftingActivity(item?.kind, item?.uuid);
  const { refresh: refreshActivity, loading: activityLoading } = activity;

  // A finished (re)generation writes a new blob version - refresh the list so
  // the new snapshot (and the rail's count) show up without reopening it.
  useEffect(() => {
    if (streamingState === 'completed') refreshVersions();
  }, [streamingState, refreshVersions]);

  // Once content has loaded, fetch the lossless editor JSON sidecar (if any).
  // The backend only returns it when it still matches the current markdown, so
  // it's safe to prefer it for editing; otherwise the editor uses the markdown.
  useEffect(() => {
    if (streamingState === 'completed' && item) {
      dispatch(fetchDraftingEditorJson({ kind: item.kind, uuid: item.uuid }));
    }
  }, [streamingState, item, dispatch]);

  // Closing the pane leaves the preview: the banner explaining that this is an
  // old version lives in there, so a preview with the pane shut is a document
  // that is silently not the current one.
  const versionsPaneOpen = panels.layout.slots.versions?.visible ?? false;
  useEffect(() => {
    if (!versionsPaneOpen && previewVersionId) selectVersion(null);
  }, [versionsPaneOpen, previewVersionId, selectVersion]);

  // When a (re)generation kicks off (status → queued/processing):
  //  1. drop any version preview so the live view is shown, and
  //  2. reset streamingState to 'idle' so the content-stream effect RE-OPENS
  //     when generation completes. Without this reset, streamingState stays
  //     'completed' from the previous load (the effect only resets on uuid
  //     change), the guard blocks re-streaming, and the viewer keeps showing
  //     the pre-regen content even though a new version was saved.
  useEffect(() => {
    if (item?.status === 'processing' || item?.status === 'queued') {
      selectVersion(null);
      dispatch(clearStreamingState());
    }
  }, [item?.status, dispatch, selectVersion]);

  // PubSub lifecycle (join / leave / safety re-fetch / event dispatch) is
  // owned by the hook - see `hooks/useDraftingPubSub`.
  const pubsub = useDraftingPubSub(item?.kind, item?.uuid);

  // Shared by the source banner and the intelligence panel: a queued re-run
  // marks generating now, joins the live group, and reconciles with a refetch.
  const handleDraftRerunQueued = useCallback((rerunPubsub?: { group: string; correlation_id: string }) => {
    if (!item) return;
    dispatch(markGenerating({ kind: item.kind, uuid: item.uuid, group: rerunPubsub?.group }));
    if (rerunPubsub?.group) {
      pubsub.join(rerunPubsub.group, rerunPubsub.correlation_id || item.uuid);
    }
    dispatch(fetchDraftingItem({ kind: item.kind, uuid: item.uuid }));
  }, [item, dispatch, pubsub]);

  const handleRetry = useCallback(() => {
    const knownKind = (item ?? cachedItem)?.kind;
    if (knownKind) {
      dispatch(fetchDraftingItem({ kind: knownKind, uuid: draftingUuid }));
    } else {
      dispatch(fetchDraftingItem({ kind: 'draft', uuid: draftingUuid }));
    }
  }, [dispatch, draftingUuid, item, cachedItem]);

  // Fetch on mount / uuid change. If cache says draft, fetch as draft;
  // else try draft and fall back to strategy on 404.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const knownKind = cachedItem?.kind;
      if (knownKind) {
        dispatch(fetchDraftingItem({ kind: knownKind, uuid: draftingUuid }));
        return;
      }
      // Unknown: try draft first, then fall back to strategy.
      const result = await dispatch(fetchDraftingItem({ kind: 'draft', uuid: draftingUuid }));
      if (!cancelled && fetchDraftingItem.rejected.match(result)) {
        await dispatch(fetchDraftingItem({ kind: 'strategy', uuid: draftingUuid }));
      }
    })();
    return () => {
      cancelled = true;
      dispatch(clearCurrentItem());
    };
  }, [dispatch, draftingUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
    Open the content stream once the draft is 'completed', and RE-open it if
    the content is ever dropped.

    THE LOADING LOOP. Three separate things reset `streamingState` back to
    'idle' after content has already arrived and rendered: `clearCurrentItem`
    on a uuid change, `clearStreamingState` when a regeneration starts, and
    `fetchDraftingItem.fulfilled` whenever it decides the item changed (which
    it does on any refetch that finds no previous item - and the page refetches
    routinely: the versions load, the activity refresh, the PubSub safety
    re-fetch). Whichever fired, the canvas went back to a skeleton and STAYED
    there, because the effect was keyed only on which draft it was streaming —
    and that had not changed, so nothing reopened anything.

    So `streamingState` is a TRIGGER here, not a guard: idle plus a finished
    draft means "there is nothing to show and something to fetch", and that is
    exactly when a stream should open. `streamingStarted` moves it off idle
    synchronously, so this cannot open twice, and 'completed' and 'error' are
    both terminal - a resolved document stays resolved.

    THE ABORT IS DELIBERATELY NOT HERE. Tearing down on every state change is
    what broke this the first time: the effect would abort the very stream it
    had just opened. Aborting belongs to a change of SUBJECT, so it lives in
    its own effect below, keyed on the draft alone.
  */
  const streamKind = item?.kind;
  const streamUuid = item?.uuid;
  const streamReady = item?.status === 'completed';
  const streamAbortRef = useRef<{ key: string; controller: AbortController } | null>(null);

  /**
   * Reading mode - the document, full width and full height.
   *
   * The same affordance a review has, and for the same reason: a drafted
   * document is meant to be read, and the panes, the tab bar and the
   * breadcrumbs are all chrome around that. Esc collapses (DetailFrame owns
   * the key), and everything comes back.
   */
  const [isExpanded, setIsExpanded] = useState(false);
  // Review-against-playbook: pick a playbook, then start a review of THIS draft.
  const [showReviewPicker, setShowReviewPicker] = useState(false);
  const [startingReview, setStartingReview] = useState(false);
  /*
    RENAMING. `patchDrafting` has existed in the slice since the surface was
    built and nothing ever dispatched it, so a drafted document's name was
    write-once: whatever the create wizard sent - which, for a draft built on
    one of your own structure templates, is the TEMPLATE's name, pre-filled at
    the picker and easy to walk past on the details step. A wrong name was
    then unfixable in the product, which is what makes it read as "I set a
    name and it never got set".

    An input in place of the title, rather than a modal: the name is one short
    string and it is already on screen, so editing it in place is the whole
    interaction. Enter or blur commits, Escape restores.
  */
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!streamReady || !streamUuid || !streamKind) return;
    if (streamingState !== 'idle') return;

    const controller = new AbortController();
    streamAbortRef.current = { key: `${streamKind}:${streamUuid}`, controller };
    // The stream helper honors the server's Content-Type (markdown → text,
    // DOCX → binary blob), so both standalone and matter-mode documents use one
    // identical path - no matter-binding guess needed here.
    openDraftingContentStream({
      kind: streamKind,
      uuid: streamUuid,
      dispatch,
      signal: controller.signal,
    });
  }, [dispatch, streamUuid, streamKind, streamReady, streamingState]);

  /*
    Abort on a change of SUBJECT - a different draft, or leaving the page.
    Never on a change of state, which is the whole point of separating them.
  */
  useEffect(() => {
    const key = `${streamKind}:${streamUuid}`;
    return () => {
      if (streamAbortRef.current?.key === key) {
        streamAbortRef.current.controller.abort();
        streamAbortRef.current = null;
      }
    };
  }, [streamUuid, streamKind]);

  // Hydration: if the item lands in processing/queued (e.g. user refreshed
  // mid-generation OR generate was triggered by another component like the
  // Customize panel), rejoin the live PubSub group surfaced by the backend.
  useEffect(() => {
    if (!item) return;
    if (item.status !== 'processing' && item.status !== 'queued') return;
    if (!item.pubsub?.group) return;
    const correlationId = item.correlation_id ?? item.uuid;
    pubsub.join(item.pubsub.group, correlationId);
  }, [item?.status, item?.pubsub?.group, item?.correlation_id, item?.uuid, pubsub]);

  // Surface a one-shot failure toast when status transitions to 'failed'.
  const lastFailedUuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!item) return;
    if (item.status !== 'failed') return;
    if (lastFailedUuidRef.current === item.uuid) return;
    lastFailedUuidRef.current = item.uuid;
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: t('Generation failed'),
      showModal: true,
      duration: 4000,
    }));
  }, [item?.status, item?.uuid, dispatch, t]);

  const handleGenerate = useCallback(async () => {
    if (!item) return;
    const result = await dispatch(generateDrafting({ kind: item.kind, uuid: item.uuid }));
    if (!generateDrafting.fulfilled.match(result)) return;

    const { pubsub: pubsubInfo } = result.payload;
    if (!pubsubInfo?.group) return;

    // The generate response uses the same correlation id the AI engine will
    // stamp on its PubSub events. Falls back to item uuid for safety.
    const correlationId = item.correlation_id ?? item.uuid;
    await pubsub.join(pubsubInfo.group, correlationId);
  }, [dispatch, item, pubsub]);

  const handleDownload = useCallback(async (format: 'docx' | 'pdf' = 'docx') => {
    if (!item) return;
    const filename = draftTitle(item, t('Untitled draft'));
    const result = await dispatch(downloadDrafting({ kind: item.kind, uuid: item.uuid, filename, format }));
    if (downloadDrafting.rejected.match(result)) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: result.payload || t('Failed to download document'),
        showModal: true,
        duration: 5000,
      }));
    }
  }, [dispatch, item, t]);

  /*
    CLM regression (#36): "Track as contract" used to exist only in the header's
    DocumentIntelligence panel, which returns null for a plain draft (one with no
    template/playbook lineage) - so plain drafts had no way to be tracked at all.
    This replicates the panel's `markAsContract('draft', …)` action as a quick
    action, available for every completed draft regardless of lineage.
  */
  const handleTrackAsContract = useCallback(async () => {
    if (!item) return;
    try {
      await markAsContract('draft', item.uuid);
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'success', duration: 4000,
        message: t('Tracking as a contract — extracting its lifecycle facts. See it under Contracts.'),
      }));
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not track this as a contract. Please try again.'),
      }));
    }
  }, [item, dispatch, t]);

  /*
    #37 quick action: re-run this draft against the latest version of its source
    structure template, reusing the SAME endpoint DocumentIntelligence calls
    (`rerunDraftFromSource`) and the SAME queued-rerun wiring (`handleDraftRerunQueued`).
    No new backend call is introduced.
  */
  const handleRefreshFromTemplate = useCallback(async () => {
    if (!item) return;
    try {
      const res = await rerunDraftFromSource(item.uuid);
      if (res.status === 'queued') {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'success', duration: 4000,
          message: t('Refreshing this draft from the updated template…'),
        }));
        handleDraftRerunQueued(res.pubsub);
      } else if (res.status === 'no_baseline') {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'warning', duration: 6000,
          message: res.reason || t('This draft predates change-tracking — regenerate it to apply the current template.'),
        }));
      } else {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'info', duration: 4000,
          message: t('This draft is already up to date with its template.'),
        }));
      }
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not refresh from the template. Please try again.'),
      }));
    }
  }, [item, dispatch, t, handleDraftRerunQueued]);

  const startRename = useCallback(() => {
    if (!item) return;
    // Seed with the STORED name, not the displayed title: a document showing
    // the "Untitled draft" placeholder must open an empty box, not one
    // pre-filled with the placeholder itself.
    setNameDraft(item.name ?? '');
    setRenaming(true);
  }, [item]);

  const cancelRename = useCallback(() => setRenaming(false), []);

  const commitRename = useCallback(async () => {
    if (!item) return;
    const next = nameDraft.trim();
    setRenaming(false);
    // No-op when nothing changed - including the empty→empty case, so simply
    // opening and closing the box never writes.
    if (next === (item.name ?? '').trim()) return;
    const result = await dispatch(patchDrafting({
      kind: item.kind,
      uuid: item.uuid,
      // Clearing the box clears the name; the title falls back to the type.
      fields: { name: next || null },
    }));
    if (patchDrafting.rejected.match(result)) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: (result.payload as string) || t('Could not rename this document'),
        showModal: true,
        duration: 4000,
      }));
    }
  }, [dispatch, item, nameDraft, t]);

  const handleDelete = useCallback(() => {
    if (!item) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Delete document'),
      message: t('This document will be permanently removed.'),
      subText: t('This action cannot be undone.'),
      variant: 'danger',
      confirmLabel: t('Delete'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      onConfirm: async () => {
        const result = await dispatch(deleteDrafting({ kind: item.kind, uuid: item.uuid }));
        dispatch(hideModal());
        if (deleteDrafting.fulfilled.match(result)) {
          dispatch(showFeedbackModal({ modalType: 'success', message: t('Document deleted'), showModal: true, duration: 3000 }));
          history.push('/drafting');
        } else {
          dispatch(showFeedbackModal({ modalType: 'error', message: t('Failed to delete document'), showModal: true, duration: 3000 }));
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  }, [dispatch, item, t, history]);

  /**
   * ACCESS - the same wiring Tables has, behind `DRAFTING_SHARING_ENABLED`.
   *
   * The pane is the READ and the shared side panel is the WRITE, so this is
   * the ONE payload both the pane's footer button and the header stack lead
   * to. Nothing here runs while the flag is off: the roster is never fetched,
   * the pane is not declared, and the stack renders nothing.
   */
  const sharingOn = DRAFTING_SHARING_ENABLED;

  useEffect(() => {
    if (!sharingOn || !item?.uuid) return;
    dispatch(fetchDraftingCollaborators({ kind: item.kind, uuid: item.uuid }));
  }, [dispatch, sharingOn, item?.kind, item?.uuid]);

  const handleManageAccess = useCallback(() => {
    if (!item) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage access'),
      resourceUuid: item.uuid,
      resourceType: 'draft',
      collaborators: item.shared_with ?? [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addDraftingCollaborator({
          kind: item.kind, uuid, collaboratorIdOrUuid: userUuid, access,
        }));
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeDraftingCollaborator({
          kind: item.kind, uuid, collaboratorUuid: collabUuid,
        }));
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateDraftingCollaboratorAccess({
          kind: item.kind, uuid, collaboratorUuid: collabUuid, access,
        }));
      },
      onUpdate: () => {
        dispatch(fetchDraftingCollaborators({ kind: item.kind, uuid: item.uuid }));
      },
    }));
  }, [dispatch, t, item, userInfo?.UserUID]);

  // Editor user object - id is best-effort; for awareness dedup we use
  // email which is stable per user.
  const editorUser = useMemo(() => ({
    id: userCtx.email || 'anon',
    name: `${userCtx.firstName} ${userCtx.lastName}`.trim() || userCtx.email || 'You',
    email: userCtx.email,
  }), [userCtx]);

  // Stable preview object so the memoized DraftingCanvas isn't re-rendered by a
  // fresh object identity every render (only when the preview actually changes).
  const canvasPreview = useMemo(
    () => (previewVersionId
      ? { versionId: previewVersionId, content: previewContent, loading: previewLoading }
      : null),
    [previewVersionId, previewContent, previewLoading],
  );

  // ─── Single status badge ─────────────────────────────────────
  // One badge slot covering the whole "working → loading preview" timeline,
  // so React reconciles the SAME element across phases instead of unmounting
  // the amber badge and mounting a separate blue one (which blinked out in
  // the one-frame gap between generation finishing and the content stream
  // starting). The 'loading' phase deliberately spans streamingState idle →
  // streaming so there's never a frame with no badge.
  //
  // Declared BEFORE the early returns below so the hook order is stable.
  const statusBadge = useMemo((): {
    color: BadgeColor; icon: React.ReactNode; label: string; suffix?: string;
  } | null => {
    const status = item?.status;
    if (!status) return null;
    const processing = status === 'processing' || status === 'queued'
      || (isGenerating && status !== 'completed' && status !== 'failed');
    if (processing) {
      return {
        color: 'amber',
        // Generation IS an AI process - the one place the logo belongs.
        icon: processingProgress === null
          ? <BrandLoader size="xs" className="!h-[13px] !w-[13px]" />
          : <PiSparkleLight size={13} className="animate-fadePulse" />,
        label: processingMessage || (processingProgress === null ? t('Starting…') : t('Working…')),
        suffix: processingProgress !== null && processingProgress > 0
          ? `${Math.round(processingProgress)}%` : undefined,
      };
    }
    if (status === 'completed' && streamingState !== 'completed' && streamingState !== 'error') {
      return {
        color: 'sky',
        icon: <PiSparkleLight size={13} className="animate-fadePulse" />,
        label: t('Loading preview…'),
        suffix: contentBytesReceived > 0 ? `${(contentBytesReceived / 1024).toFixed(1)} KB` : undefined,
      };
    }
    return null;
  }, [item?.status, isGenerating, processingProgress, processingMessage, streamingState, contentBytesReceived, t]);

  // ─── Loading / not-found / error ───────────────────────────────
  // While the item details are still being fetched, show the surface's own
  // silhouette - the canvas with its docked panes - so the open sequence
  // (fetch item → open stream → render) is one continuous skeleton→page swap.
  if (!item && itemLoading) {
    return <Skeleton variant="detail" surface={DRAFTING_SURFACE} />;
  }
  if (!item && (error || itemError)) {
    const statusLabel = itemError?.status ? `${itemError.status} ` : '';
    const is404 = itemError?.status === 404;
    const heading = is404 ? t('Document not found') : t('Failed to load document');
    const detail = itemError?.detail;
    const message = itemError?.message ?? error;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <GlassCard tone="danger" padding="lg" className="w-full max-w-sm">
          <p className="mb-1 text-sm font-medium text-danger-800 dark:text-danger-300">
            {statusLabel}{heading}
          </p>
          {detail && (
            <p className="mb-1 text-xs text-danger-700 dark:text-danger-400">{detail}</p>
          )}
          {message && message !== heading && (
            <p className="text-xs text-danger-600 dark:text-danger-400">{message}</p>
          )}
        </GlassCard>
        <div className="flex items-center gap-3">
          <Button variant="primary" size="small" onClick={handleRetry} icon={<PiArrowClockwiseLight size={14} />}>
            {t('Retry')}
          </Button>
          <Button variant="neutral" size="small" onClick={() => history.push('/drafting')}>
            ← {t('Back to drafting')}
          </Button>
        </div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-fg-muted">{t('Document not found.')}</p>
        <Button variant="neutral" size="small" onClick={() => history.push('/drafting')}>
          ← {t('Back to drafting')}
        </Button>
      </div>
    );
  }

  // First release ships read-only (no in-app editing, no collab socket).
  const canEdit = !DRAFTING_READ_ONLY && item.status === 'completed';
  // Treat an item as processing whenever a generation is in flight for it —
  // `generatingIds` is set the moment generate is queued and survives
  // clearCurrentItem, so it bridges the create → navigate → first-GET window
  // where the DB row may still read 'not_generated'. Without this, the detail
  // view flashes the "Click Generate" placeholder for the first few seconds.
  const isProcessing =
    item.status === 'processing'
    || item.status === 'queued'
    || (isGenerating && item.status !== 'completed' && item.status !== 'failed');

  /*
    Name, type, placeholder - and NEVER the raw `document_type_id`, which is
    `template_<uuid>` for a draft built on a user's own structure template.
    See `../draftLabels` for why that id is not a fallback name.
  */
  const untitledLabel = t('Untitled draft');
  const documentTitle = draftTitle(item, untitledLabel);
  // The document type, but never twice: an unnamed document already shows its
  // type as the title, and an opaque id is shown nowhere at all.
  const typeLabel = draftTypeLabel(item, documentTitle, item.category);
  const updatedTime = feedTimeLabel(item.updated_at ?? item.created_at ?? undefined, t);
  const hasVersions = item.status === 'completed';
  // A tab someone cannot use must not decide what the canvas shows.
  const editing = activeTab === 'edit' && canEdit;

  // ─── The canvas's tabs: the two reading modes ────────────────
  const modeTabs: TabItem[] = [
    { label: t('View'), value: 'view', icon: <PiEyeLight /> },
    ...(DRAFTING_READ_ONLY
      ? []
      : [{
          label: t('Edit'),
          value: 'edit',
          icon: <PiPencilSimpleLight />,
          disabled: !canEdit,
          tooltip: canEdit ? undefined : t('Editing opens once the document has been generated'),
        }]),
  ];

  /** The pane header's own control: one icon button, same shape on every pane. */
  const paneAction = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    busy?: boolean,
  ) => (
    <Button
      variant="neutral"
      size="small"
      ariaLabel={label}
      tooltip={label}
      icon={icon}
      isLoading={busy}
      onClick={onClick}
    />
  );

  const panes: DetailPane<DraftingPanelId>[] = [
    {
      id: 'access',
      title: t('Linked matters'),
      icon: <PiBriefcaseLight />,
      body: () => <DraftingAccessPane item={item} />,
    },
    ...(sharingOn
      ? [{
          id: 'people' as const,
          title: t('Access'),
          icon: <PiUsersLight />,
          /* The pane is the READ; its one footer button is the WRITE. No
             "Manage access" in the ⋮ menu - see AccessPane's header note. */
          body: () => (
            <AccessPane
              collaborators={item.shared_with ?? []}
              onManageAccess={handleManageAccess}
              canManage
            />
          ),
        }]
      : []),
    ...(hasVersions
      ? [{
          id: 'versions' as const,
          title: t('Version history'),
          icon: <PiClockCounterClockwiseLight />,
          actions: paneAction(
            t('Refresh'),
            <PiArrowsClockwiseLight size={14} />,
            refreshVersions,
            versions.loading,
          ),
          body: () => <DraftingVersionsPane versions={versions} />,
        }]
      : []),
    {
      id: 'activity',
      title: t('Activity'),
      icon: <PiPulseLight />,
      actions: paneAction(
        t('Refresh'),
        <PiArrowsClockwiseLight size={14} />,
        refreshActivity,
        activityLoading,
      ),
      /* A thunk: built only while the pane is actually on screen. */
      body: () => <DraftingActivityPane activity={activity} />,
    },
    /*
      AI USAGE - for DOCUMENTS only.

      The read API's entity vocabulary is matter|table|draft|review|research|
      template|datasource: a STRATEGY has no entity type in it, and sending
      `draft` for one would ask the usage service about a record that is not
      the strategy. So the pane is passed conditionally, the same way `people`
      and `versions` are - on a strategy the pane id simply never renders, and
      neither does its rail pill.
    */
    ...(item.kind === 'draft'
      ? [{
          id: 'aiUsage' as const,
          title: t('AI usage'),
          icon: <PiSparkleLight />,
          /* A thunk: the panel fetches on mount, so a document nobody asks
             the cost of never costs a request. */
          body: () => (
            <AIUsagePanel
              entityType="draft"
              entityId={item.uuid}
              entityName={item.name || ''}
            />
          ),
        }]
      : []),
  ];

  /*
    ── Reading mode ──────────────────────────────────────────────────

    The document and one slim bar, exactly as a review expands. The canvas
    is the only child; the panes, the tab bar and the meta line come back on
    collapse. There is no Close, deliberately: collapsing exits a MODE, and
    leaving the document is the breadcrumb's job.
  */
  if (isExpanded) {
    return (
      <DetailFrame
        breadcrumbs={[
          { name: t('Drafting'), href: '/drafting', current: false },
          { name: documentTitle, href: `/drafting/${item.uuid}`, current: true },
        ]}
        isFullscreen
        fileInfo={{ name: documentTitle, type: typeLabel || undefined }}
        onExitFullscreen={() => setIsExpanded(false)}
      >
        <div className="flex flex-1 flex-col min-h-0 bg-white dark:bg-surface">
          <DraftingCanvas
            status={item.status}
            streamingState={streamingState}
            contentBytesReceived={contentBytesReceived}
            currentContent={currentContent}
            currentContentBlob={currentContentBlob}
            initialJson={currentEditorJson}
            matterUuid={item.uuid}
            documentId={item.document_type_id}
            editorUser={editorUser}
            readOnly={!editing}
            onSave={handleSaveContent}
            isProcessing={isProcessing}
            processingMessage={processingMessage}
            processingProgress={processingProgress}
            preview={canvasPreview}
          />
        </div>
      </DetailFrame>
    );
  }

  return (
    <>
    <DetailFrame
      breadcrumbs={[
        // Drafting is a top-level entity - always root the breadcrumb at
        // Drafting, regardless of matter attachment. Any connected matter is
        // surfaced in the Linked matters pane.
        { name: t('Drafting'), href: '/drafting', current: false },
        { name: documentTitle, href: `/drafting/${item.uuid}`, current: true },
      ]}
      onBack={() => history.push('/drafting')}
      actions={
        item.status === 'not_generated'
          ? [{ label: t('Generate'), onClick: handleGenerate, icon: <PiSparkleLight size={16} /> }]
          : []
      }
      dropdownLabel="Actions"
      dropdownActions={[
        {
          label: t('Customize & regenerate'),
          onClick: () =>
            dispatch(
              showSidePanel({
                type: 'draftingCustomize',
                showSidePanel: true,
                // The width scale, not a one-off '68%' - see sidePanelReducer.
                size: 'lg',
                message: t('Customization workspace'),
                subMsg: draftTitle(item, t('Untitled draft')),
                icon: <PiSlidersHorizontalLight size={20} className="text-primary-600" />,
                kind: item.kind,
                draftingUuid: item.uuid,
                existingItem: item,
              })
            ),
          icon: <PiSlidersHorizontalLight size={16} />,
        },
        ...(item.status === 'completed'
          ? [{
              label: t('Download (Word)'),
              onClick: () => handleDownload('docx'),
              icon: <PiDownloadSimpleLight size={16} />,
            }, {
              label: t('Download (PDF)'),
              onClick: () => handleDownload('pdf'),
              icon: <PiDownloadSimpleLight size={16} />,
            }]
          : []),
        ...(item.status === 'completed' && item.kind === 'draft'
          ? [{
              label: t('Time saved'),
              onClick: () => dispatch(showSidePanel({
                type: 'timeSaved', showSidePanel: true, size: 'sm',
                message: t('Time saved'), subMsg: draftTitle(item, t('Untitled draft')),
                kind: 'draft', uuid: item.uuid,
              })),
              icon: <PiClockLight size={16} />,
            }]
          : []),
        ...(item.status === 'completed' && item.kind === 'draft'
          ? [{
              label: t('Run legislation scan'),
              onClick: () =>
                dispatch(
                  showSidePanel({
                    type: 'scan',
                    showSidePanel: true,
                    size: 'md',
                    message: t('Legislation scan'),
                    subMsg: draftTitle(item, t('Untitled draft')),
                    target: { kind: 'draft', draftUuid: item.uuid },
                  })
                ),
              icon: <PiScalesLight size={16} />,
            }]
          : []),
        ...(item.status === 'completed' && item.kind === 'draft'
          ? [{
              label: t('Review against playbook'),
              onClick: () => setShowReviewPicker(true),
              icon: <PiListChecksLight size={16} />,
            }, {
              // #37 - re-run from the source template (handler: rerunDraftFromSource).
              label: t('Refresh from template'),
              onClick: handleRefreshFromTemplate,
              icon: <PiArrowsClockwiseLight size={16} />,
            }, {
              // #36 - CLM regression fix: available for plain drafts too.
              label: t('Track as contract'),
              onClick: handleTrackAsContract,
              icon: <PiScrollLight size={16} />,
            }]
          : []),
        {
          label: t('Rename'),
          onClick: startRename,
          icon: <PiPencilSimpleLight size={16} />,
        },
        {
          label: t('Activity log'),
          onClick: () => { panels.setVisible('activity', true); panels.focus('activity'); },
          icon: <PiClockCounterClockwiseLight size={16} />,
        },
        {
          label: t('Delete'),
          onClick: handleDelete,
          icon: <PiTrashLight size={16} />,
          variant: 'destructive'
        },
      ]}
      additionalHeaderElements={
        <>
          {/* DetailFrame's own `collaborators` stack is decorative - this one
              RAISES THE ACCESS PANE, which is the page's read surface. */}
          {sharingOn && (
            <CollaboratorStack
              collaborators={item.shared_with ?? []}
              onClick={() => { panels.setVisible('people', true); panels.focus('people'); }}
              testId="drafting-collaborator-stack"
            />
          )}
          {item.kind === 'draft' && item.uuid && <TimeSavedChip kind="draft" uuid={item.uuid} />}
          {/* Document intelligence lives IN the header now (was a loose badge
              stacked above the document). */}
          {item.kind === 'draft' && (
            <DocumentIntelligence
              draftUuid={item.uuid}
              onRerunQueued={handleDraftRerunQueued}
            />
          )}
          {statusBadge && (
            <Badge color={statusBadge.color} icon={statusBadge.icon}>
              <span className="whitespace-nowrap">{statusBadge.label}</span>
              {statusBadge.suffix && (
                <span className="opacity-70">{statusBadge.suffix}</span>
              )}
            </Badge>
          )}
        </>
      }
    >
      <div className="flex flex-1 flex-col min-h-0">
        {/* The "update available" state lives in the header intelligence pill now
            (was an awkward banner between the header and the document). */}
        {/* ONE ROW. The document's own line - what this is, what it belongs
            to, how old it is - used to be a block STACKED above the tab strip,
            and its meta line wrapped on top of that. It is the tab bar's
            `leading` slot now, so the document, the reading modes and the pane
            rail share a single row above the canvas. The meta line no longer
            wraps: it truncates, and the lowest-value facts (the type, the word
            count) drop out on a narrow window instead of taking a second row. */}
        <div className="w-full flex-shrink-0">
          <DetailTabBar
            tabs={modeTabs}
            activeTab={editing ? 'edit' : 'view'}
            onTabClick={goToTab}
            leading={(
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  aria-hidden
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full
                             border border-champagne/50 bg-champagne/10 text-champagne"
                >
                  <PiFileTextLight className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  {renaming ? (
                    <input
                      ref={renameInputRef}
                      autoFocus
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                      }}
                      maxLength={200}
                      aria-label={t('Document name')}
                      placeholder={t('Untitled draft')}
                      // Same size and weight as the title it replaces; the
                      // negative margins cancel its padding so nothing shifts.
                      className="-mx-1.5 -my-0.5 w-full min-w-0 rounded border border-champagne/60 bg-surface px-1.5 py-0.5
                                 text-sm font-medium text-fg-strong outline-none
                                 focus-visible:ring-2 focus-visible:ring-champagne/60"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={startRename}
                      title={t('Rename')}
                      className="group flex min-w-0 max-w-full items-center gap-1.5 rounded text-start
                                 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
                    >
                      <h1 className="truncate text-sm font-medium text-fg-strong" title={documentTitle}>
                        {documentTitle}
                      </h1>
                      <PiPencilSimpleLight
                        aria-hidden
                        className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted opacity-0 transition-opacity
                                   group-hover:opacity-100 group-focus-visible:opacity-100"
                      />
                    </button>
                  )}
                  <p className="mt-0.5 flex flex-nowrap items-center gap-x-2 overflow-hidden text-[11px] text-fg-muted">
                    <span className="flex-shrink-0">
                      {item.kind === 'strategy' ? t('Strategy') : t('Draft')}
                    </span>
                    {typeLabel && (
                      <>
                        <MetaDot className="hidden lg:block" />
                        <span className="hidden truncate lg:inline">{typeLabel}</span>
                      </>
                    )}
                    {/* UNTOUCHED. Same button, same target, same pane behind
                        it - the linkage is out of scope for this pass. */}
                    {item.matter_title && (
                      <>
                        <MetaDot />
                        <button
                          type="button"
                          disabled={!item.matter_uuid}
                          onClick={() => item.matter_uuid && history.push(`/matters/${item.matter_uuid}`)}
                          className="inline-flex max-w-[220px] items-center gap-1 truncate transition-colors
                                     hover:text-fg-body disabled:cursor-default disabled:hover:text-fg-muted"
                        >
                          <PiBriefcaseLight className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{item.matter_title}</span>
                        </button>
                      </>
                    )}
                    {updatedTime.label && (
                      <>
                        <MetaDot className="hidden md:block" />
                        <span className="hidden whitespace-nowrap md:inline" title={updatedTime.title}>
                          {t('Updated')} {updatedTime.label}
                        </span>
                      </>
                    )}
                    {item.word_count ? (
                      <>
                        <MetaDot className="hidden xl:block" />
                        <span className="hidden whitespace-nowrap xl:inline">
                          {t('{{count}} words', { count: item.word_count })}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            )}
            actions={(
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="drafting-expand"
                  onClick={() => setIsExpanded(true)}
                  title={t('Expand')}
                  aria-label={t('Expand')}
                  className="flex-shrink-0 p-1.5 rounded-md text-fg-muted
                             hover:bg-surface-sunken/70 hover:text-champagne
                             transition-colors duration-200 ease-slide"
                >
                  <PiArrowsOutLight className="w-4 h-4" />
                </button>
                <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                items={DRAFTING_SURFACE.railItems({
                  t,
                  icons: {
                    access: <PiBriefcaseLight />,
                    people: <PiUsersLight />,
                    versions: <PiClockCounterClockwiseLight />,
                    // Not the clock again - two rail pills with the same glyph
                    // read as the same pane opened twice.
                    activity: <PiPulseLight />,
                    aiUsage: <PiSparkleLight />,
                  },
                  // The snapshot count and the share list's own size, so each
                  // pill is right whether or not its pane has been opened.
                  badges: {
                    versions: versionCount,
                    people: item.shared_with?.length ?? 0,
                  },
                  // Nothing to version until the document has been generated,
                  // and a strategy has no entity type in the usage API - so
                  // neither pill is drawn for a pane the page does not pass.
                  omit: [
                    ...(hasVersions ? [] : ['versions' as const]),
                    ...(item.kind === 'draft' ? [] : ['aiUsage' as const]),
                  ],
                })}
                />
              </div>
            )}
          />
        </div>

        {/* The canvas IS the paper here, so it runs white to the shell's edge
            instead of framing a white sheet on the grey app canvas - the
            three-deep white-on-white-on-grey nesting this page used to draw. */}
        <DetailPageSurface
          surface={DRAFTING_SURFACE}
          layoutApi={panels}
          panes={panes}
          canvasClassName="bg-white dark:bg-surface"
          canvasBleed
        >
          <DraftingCanvas
            status={item.status}
            streamingState={streamingState}
            contentBytesReceived={contentBytesReceived}
            currentContent={currentContent}
            currentContentBlob={currentContentBlob}
            initialJson={currentEditorJson}
            matterUuid={item.uuid}
            documentId={item.document_type_id}
            editorUser={editorUser}
            readOnly={!editing}
            onSave={handleSaveContent}
            isProcessing={isProcessing}
            processingMessage={processingMessage}
            processingProgress={processingProgress}
            preview={canvasPreview}
          />
        </DetailPageSurface>
      </div>
    </DetailFrame>
    {showReviewPicker && item && (
      <PlaybookPickerModal
        selectedPlaybookUuid=""
        onClose={() => setShowReviewPicker(false)}
        onSelect={async (playbook) => {
          if (!playbook || startingReview) return;
          setStartingReview(true);
          try {
            const res = await startReviewFromDraft(item.uuid, playbook.uuid);
            if (res?.session_uuid) {
              dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: t('Review started'), duration: 5000 }));
              history.push(`/reviews/${res.session_uuid}`);
            } else {
              dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: res?.error || t('Could not start the review'), duration: 6000 }));
            }
          } catch (e: any) {
            dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: e?.response?.data?.error || t('Could not start the review'), duration: 6000 }));
          } finally {
            setStartingReview(false);
          }
        }}
      />
    )}
    </>
  );
}

export default DraftingDetail;
